import { Browser } from '@capacitor/browser'
import {
  Avatar,
  Box,
  Button,
  FormHelperText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import get from 'lodash/get'
import {
  Dispatch,
  forwardRef,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Control,
  FieldError,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import YouTube from 'react-youtube'
import {
  FileUploader,
  IconButton,
  Icons,
  InformationModal,
  Resource,
  Select,
  TextInputField,
  TextInputFieldProps,
  ToggleInputField,
} from 'utoniq-ui'

import {
  CreatePostInput,
  ImageResourceInput,
  Maybe,
  MembershipInterval,
  MembershipSubscriptionPlan,
  PostCategoryDetail,
  PostType,
  ResourceInput,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { Loading } from '@/components/organisms'
import { FullscreenModal } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import {
  handleError,
  tLabel,
  tMessage,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { captureError } from '@/core/errors'
import { Form, useForm, UseFormProps } from '@/core/form'
import { individualShowStory } from '@/core/individual'
import { App } from '@/core/native'
import { usePlayingResource } from '@/core/resources'

import { ConsolePostReserve } from './ConsolePostReserve'
import { PostCreateConfirmModal } from './PostCreateConfirmModal'

export * from './fragments'

export type PostCreateValues = {
  content: string
  resource?: ResourceInput | null
  thumbnailPath?: string | null
  resources?: ImageResourceInput[] | null
  categoryId: string
  membershipAccessPermission: string
  resourceType: PostType
  withTwitter: boolean
  isStory?: boolean
  publishedAt?: string
}
const schema: UseFormProps['schema'] = o => ({
  content: o.string().max(10000).label(tLabel('consolePostCreate.content')),
  resource: o.object().when('resourceType', [
    { is: 'text', then: o.any().valid({}) },
    { is: 'image', then: o.any().allow(null).optional() },
    {
      is: 'video',
      then: o
        .object({
          videoId: o
            .string()
            .optional()
            .messages({
              'string.empty': tMessage(
                'consolePostCreate.resource.video.string.empty'
              ),
            }),
          tmpPath: o
            .string()
            .optional()
            .messages({
              'string.empty': tMessage(
                'consolePostCreate.resource.video.string.empty'
              ),
            }),
        })
        .xor('videoId', 'tmpPath')
        .optional()
        .unknown(false)
        .label('ラベル'),
    },
    {
      is: 'sound',
      then: o.object({
        tmpPath: o.string().messages({
          'string.empty': tMessage(
            'consolePostCreate.resource.sound.string.empty'
          ),
        }),
      }),
    },
  ]),
  thumbnailPath: o.string().when('resourceType', {
    is: 'video',
    then: o.string().when('resource.videoId', {
      is: o.exist(),
      then: o.valid(null).messages({
        'any.only': tMessage('consolePostCreate.thumbnailPath.any.only'),
      }),
      otherwise: o.string().optional().allow(null),
    }),
    otherwise: o.string().optional().allow(null),
  }),
  categoryId: o.string().label(tLabel('consolePostCreate.categoryId')),
  resources: o.array().when('resourceType', {
    is: 'image',
    then: o
      .array()
      .items(
        o.object({
          tmpPath: o.string().messages({
            'string.empty': tMessage(
              'consolePostCreate.resource.image.string.empty'
            ),
          }),
        })
      )
      .label(tLabel('consolePostCreate.resources')),
    otherwise: o.array().optional().allow(null),
  }),
  membershipAccessPermission: o
    .string()
    .label(tLabel('consolePostCreate.membershipAccessPermission')),
  resourceType: o.string().label(tLabel('consolePostCreate.resourceType')),
  withTwitter: o
    .boolean()
    .optional()
    // ストーリー投稿の場合は X（旧 Twitter）連携不可（falseのみ許容）
    .when('isStory', {
      is: true,
      then: o
        .valid(false)
        .optional()
        .messages({
          'any.only': tMessage(
            'consolePostCreate.withTwitter.isStory.any.only'
          ),
        }),
      otherwise: o.boolean().optional(),
    })
    .label(tLabel('consolePostCreate.withTwitter')),
  isStory: o
    .boolean()
    .optional()
    .when('resourceType', [
      // 画像ポストはストーリー可（true/false/未指定 OK）
      {
        is: 'image',
        then: o.boolean().optional(),
      },
      // 動画ポストはストーリー可（true/false/未指定 OK）
      {
        is: 'video',
        then: o.boolean().optional(),
      },
      // テキスト、音声ポストはストーリー不可（false/未指定 限定）
      {
        is: o.any().valid('text', 'sound'),
        then: o
          .valid(false)
          .optional()
          .messages({
            'any.only': tMessage(
              'consolePostCreate.isStory.resourceType.any.only'
            ),
          }),
      },
    ])
    .label(tLabel('consolePostCreate.isStory')),
  publishedAt: o
    .date()
    .allow(null)
    .optional()
    .label(tLabel('consolePostCreate.publishedAt')),
})

// カテゴリリストの初期値
const defaultCategory = {
  none: 'noneCategory',
}

// ポスト公開範囲リストの初期値
const defaultPermission = {
  allUser: 'allUser',
  allMember: 'allMember',
}

const defaultValues: PostCreateValues = {
  content: '',
  resource: {},
  thumbnailPath: null,
  categoryId: defaultCategory.none,
  membershipAccessPermission: defaultPermission.allUser,
  resourceType: PostType.Text,
  withTwitter: false,
  resources: [],
  publishedAt: undefined,
  isStory: false,
}
const defaultResourceValues = {
  [PostType.Text]: {},
  [PostType.Image]: undefined,
  [PostType.Video]: { videoId: '' },
  [PostType.Sound]: { tmpPath: '' },
}

export type FileUploadInfo = {
  onDrop: (
    multiple?: boolean,
    limit?: number
  ) => Promise<Resource[] | undefined>
  progress: number
}

export const PostCreateForm: Organism<{
  resourceType?: PostType
  fileUploadInfo: FileUploadInfo
  thumbnailUploadInfo: FileUploadInfo
  isConnectedTwitter: boolean
  onSubmitPost: (inputs: CreatePostInput) => Promise<void>
  loadingCreatePost: boolean
  onConnectTwitter: () => Promise<void>
  withTwitter: boolean
  resourcesUploading: boolean
  thumbnailUploading: boolean
  artistId: string
  plans: Maybe<
    Pick<MembershipSubscriptionPlan, 'planId' | 'title' | 'interval'>
  >[]
  categories: Maybe<Pick<PostCategoryDetail, 'id' | 'name'>>[]
}> = ({
  resourceType = PostType.Text,
  fileUploadInfo,
  thumbnailUploadInfo,
  isConnectedTwitter,
  onSubmitPost,
  loadingCreatePost,
  onConnectTwitter,
  withTwitter,
  resourcesUploading,
  thumbnailUploading,
  artistId,
  plans,
  categories,
}) => {
  const { t } = useTranslation('consolePostCreate')
  const { showMessage, showError } = useMessage()
  const { router, routerPages } = useRouter()
  const [open, setOpen] = useState(false)
  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    reset,
    watch,
    control,
    setError,
    getValues,
  } = useForm<PostCreateValues>({
    schema,
    defaultValues: {
      ...defaultValues,
      resource: defaultResourceValues[resourceType],
      resourceType,
      withTwitter,
      isStory: false,
    },
  })

  const isWeb = App.getPlatform() === 'web'
  const { ref: withTwitterRef, ...restWithTwitter } = register('withTwitter')
  const { ref: isStoryRef, ...restIsStory } = register('isStory')
  const { ref, ...rest } = register('membershipAccessPermission')
  const withTwitterWatch = watch('withTwitter', withTwitter)
  const isStoryWatch = watch('isStory', false)

  useEffect(() => {
    if (withTwitterWatch && !isConnectedTwitter) {
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withTwitterWatch])

  // ストーリー投稿が有効になった場合、withTwitter を false に設定
  useEffect(() => {
    if (isStoryWatch && withTwitterWatch) {
      setValue('withTwitter', false, { shouldValidate: true })
    }
  }, [isStoryWatch, withTwitterWatch])

  useEffect(() => {
    reset({
      ...defaultValues,
      resource: defaultResourceValues[resourceType],
      resourceType,
      withTwitter,
    })
    register('resource')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, resourceType, withTwitter])

  const [confirmModal, setConfirmModal] = useState<
    { confirmAction: (confirmed: boolean) => void } | undefined
  >()
  const onCreatePost = async () => {
    const confirmed = await new Promise<boolean>(resolve => {
      setConfirmModal({
        confirmAction: resolve,
      })
    })
    // キャンセルされた場合、モーダルを閉じて終了
    if (!confirmed) {
      setConfirmModal(undefined)
      return
    }
    const { membershipAccessPermission, ...inputs } = getValues()

    // 投稿確定した場合、投稿処理を実行し終了後モーダルを閉じる
    return onSubmitPost({
      ...inputs,
      /**
       * 公開範囲のキーからisPremiumとplanIdの値をミューテーション引数に割り当てる
       * プランを指定しない場合はplanId: nullになる
       */
      isPremium: membershipAccessPermission !== defaultPermission.allUser,
      planId: Object.values(defaultPermission).includes(
        membershipAccessPermission
      )
        ? undefined
        : membershipAccessPermission,
      // カテゴリIDがnoneCategoryの場合はカテゴリ未指定としてnullを渡す
      categoryId:
        inputs.categoryId === defaultCategory.none ? null : inputs.categoryId,
    })
      .then(() => {
        showMessage(
          t(inputs.publishedAt ? 'completePostReserve' : 'completePost'),
          'success',
          inputs.publishedAt ? (
            <Button
              variant="text"
              size="small"
              sx={{ color: 'white' }}
              onClick={() => router.push(routerPages.ConsoleUnpublishedPosts)}
            >
              {t('display')}
            </Button>
          ) : undefined
        )
        router.push(routerPages.ConsolePost)
      })
      .catch(e => {
        captureError(e, 'consolePostCreate')
        handleError<PostCreateValues>(e, setError, showError)
      })
      .finally(() => {
        setConfirmModal(undefined)
      })
  }

  // YouTube の videoId を取得
  const videoId = useWatch({
    control,
    name: 'resource.videoId',
  })

  // YouTube の videoId が変更された時
  useEffect(() => {
    if (videoId) {
      // サムネイルをリセットする
      setValue('thumbnailPath', null)
    }
  }, [videoId])

  return (
    <>
      <Form onSubmit={handleSubmit(onCreatePost)} sx={{ py: 6 }}>
        {resourceType === PostType.Image && (
          <ImageUploadCell
            control={control}
            imageUploadInfo={fileUploadInfo}
            setValue={setValue}
            error={get(errors, 'resources.message')}
            resourcesUploading={resourcesUploading}
          />
        )}
        {resourceType === PostType.Video && (
          <>
            <VideoUploadCell
              videoId={watch()?.resource?.videoId}
              setValue={setValue}
              error={errors.resource?.videoId?.message}
              videoUploadInfo={fileUploadInfo}
              resourcesUploading={resourcesUploading}
            />
            {/* YouTubeの場合（videoIdがある場合）はサムネイルアップロードを表示しない */}
            {!watch()?.resource?.videoId && (
              <ThumbnailUploadCell
                control={control}
                thumbnailUploadInfo={thumbnailUploadInfo}
                setValue={setValue}
                error={get(errors, 'thumbnailPath.message')}
                thumbnailUploading={thumbnailUploading}
              />
            )}
          </>
        )}
        {resourceType === PostType.Sound && (
          <AudioUploadCell
            audioUploadInfo={fileUploadInfo}
            setValue={setValue}
            error={errors.resource?.tmpPath?.message}
          />
        )}

        {(resourceType === PostType.Image ||
          resourceType === PostType.Video) && (
          <CellFrame sx={{ mb: 6 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Stack flex={1}>
                <Typography variant="h5">{t('postCategory')}</Typography>
              </Stack>
              <Stack>
                <Button
                  variant="text"
                  onClick={() => router.push(routerPages.ConsolePostCategories)}
                  sx={{ textAlign: 'right', alignItems: 'flex-end' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {t('editCategory')}
                  </Stack>
                </Button>
              </Stack>
            </Stack>
            <Select
              listItems={[
                ...Object.values(defaultCategory).map(value => ({
                  value: value,
                  label: t(value),
                })),
                ...(categories.length
                  ? categories.map(category => ({
                      value: category?.id ?? '',
                      label: category?.name ?? '',
                    }))
                  : []),
              ]}
              value={watch('categoryId')}
              onChange={value => {
                setValue('categoryId', value)
              }}
              formControlProps={{
                sx: {
                  width: '100%',
                  bgcolor: 'grey.50',
                  minHeight: 'auto',
                  mb: 16,
                },
              }}
            />
          </CellFrame>
        )}
        <ContentCell register={register} error={errors.content} />
        <ReserveInputCell
          artistId={artistId}
          setValue={setValue}
          value={watch('publishedAt')}
        />
        {config.platform.includes('web') &&
          /**
           * @description
           * Diica以外の場合、SNS認証機能解放
           * （DiicaはSNS連携時のUtoniqアイコンの表示をしてはいけないため、SNSリンク機能を隠す）
           * ストーリー投稿の場合は X（旧 Twitter）連携機能を非表示
           * X 上で24時間で投稿が削除されず、残り続けることを避けるため
           */
          config.applicationKey !== 'diica' &&
          !isStoryWatch && (
            <ToggleInputField
              content={{ title: t('withTwitter') }}
              checked={withTwitterWatch}
              inputRef={withTwitterRef}
              sx={{ mx: -2 }}
              {...restWithTwitter}
            />
          )}

        {/* 24時間限定公開 */}
        {individualShowStory() &&
          (resourceType === PostType.Image ||
            resourceType === PostType.Video) && (
            <ToggleInputField
              content={{ title: t('isStory'), description: t('storyDesc') }}
              checked={isStoryWatch}
              inputRef={isStoryRef}
              sx={{ mx: -2 }}
              {...restIsStory}
            />
          )}

        {/* 公開範囲を選択する */}
        <CellFrame sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t('membershipAccessPermission')}
          </Typography>
          <Select
            listItems={[
              ...Object.values(defaultPermission).map(value => ({
                value: value,
                label: t(value),
              })),
              ...plans.map(plan =>
                plan?.interval === MembershipInterval.Year
                  ? {
                      value: plan?.planId ?? '',
                      label: t('planOnlyYear', { name: plan?.title ?? '' }),
                    }
                  : {
                      value: plan?.planId ?? '',
                      label: t('planOnly', { name: plan?.title ?? '' }),
                    }
              ),
            ]}
            value={watch('membershipAccessPermission').toString()}
            onChange={value => {
              setValue('membershipAccessPermission', value)
            }}
            formControlProps={{
              sx: {
                width: '100%',
                bgcolor: 'grey.50',
                minHeight: 'auto',
                mb: 16,
              },
            }}
          />
        </CellFrame>
        <ButtonCell
          submitting={loadingCreatePost}
          resourcesUploading={resourcesUploading}
          thumbnailUploading={thumbnailUploading}
          isReserve={!!watch('publishedAt')}
        />
        <InformationModal
          open={open}
          onClose={() => {
            setOpen(false)
            setValue('withTwitter', false, { shouldValidate: true })
          }}
          title={t('signUpTwitterTitle')}
          content={t('signUpTwitterDesc')}
          buttons={
            <Button
              size="large"
              sx={{ width: 212 }}
              onClick={async () => {
                if (isWeb) {
                  onConnectTwitter()
                    .then(() => {
                      setValue('withTwitter', true)
                    })
                    .finally(() => {
                      setOpen(false)
                    })
                  return
                }

                Browser.open({
                  url: `${config.domain}${routerPages.Integrations}`,
                })

                setOpen(false)
              }}
            >
              {t('signUpTwitterBtn')}
            </Button>
          }
          cancelText={t('cancel')}
        />
      </Form>
      <PostCreateConfirmModal
        open={!!confirmModal}
        onClose={() => confirmModal?.confirmAction(false)}
        onClick={() => confirmModal?.confirmAction(true)}
        loading={loadingCreatePost}
      />
    </>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consolePostCreate')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const ButtonCell: Cell<{
  submitting: boolean
  resourcesUploading: boolean
  thumbnailUploading: boolean
  isReserve: boolean
}> = ({ submitting, resourcesUploading, thumbnailUploading, isReserve }) => {
  const { t } = useTranslation('consolePostCreate')

  return (
    <CellFrame sx={{ px: 8, mb: 8 }}>
      <Button
        type="submit"
        size="large"
        fullWidth
        loading={submitting}
        disabled={resourcesUploading || thumbnailUploading}
      >
        {!isReserve ? t('toPost') : t('toReserve')}
      </Button>
    </CellFrame>
  )
}

const ImageUploadCell: Cell<{
  imageUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<PostCreateValues>
  control: Control<PostCreateValues>
  error?: string
  resourcesUploading: boolean
}> = ({ imageUploadInfo, control, setValue, error, resourcesUploading }) => {
  const { t } = useTranslation('consolePostCreate')
  const [resources, setResources] = useState<Resource[]>([])
  const { remove, fields } = useFieldArray({
    control,
    name: 'resources',
  })

  return (
    <CellFrame sx={{ mb: 8 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Icons.Image sx={{ color: 'primary.main' }} />
          <Typography variant="h5">{t('imageUploaderTitle')}</Typography>
        </Stack>
        <IconButton.Plus
          size="small"
          bgcolor="grey.50"
          disabled={fields.length >= 10 || resourcesUploading}
          onClick={() =>
            imageUploadInfo.onDrop(true, 10 - fields.length).then(resources => {
              if (!resources) {
                return
              }
              setValue(
                'resources',
                [
                  ...fields.map(({ tmpPath }) => ({ tmpPath })),
                  ...resources
                    .map(resource => ({
                      tmpPath: resource.imageUploadPath ?? '',
                    }))
                    // get 10 items
                    .slice(0, 10),
                ],
                { shouldValidate: true }
              )
              setResources(prevState => [...prevState, ...resources])
            })
          }
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
        {t('imageUploaderDescription')}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          overflowX: 'auto',
          pt: resources.length ? 5 : 0,
          ...styleSets.hideScrollbar(),
        }}
      >
        {resources.map((resource, index) => (
          <Stack
            key={resource.id}
            sx={{ position: 'relative', bgcolor: 'grey.100' }}
          >
            <IconButton.Close
              size="small"
              bgcolor="grey.50"
              onClick={() => {
                remove(index)
                setResources(prevState =>
                  prevState.filter((_, idx) => idx !== index)
                )
              }}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
            />
            <Avatar
              variant="square"
              src={resource.imageUrl}
              sx={{
                width: 280,
                height: 200,
                img: { objectFit: 'contain' },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                bgcolor: 'rgba(255,255,255,0.6)',
                borderRadius: '40px',
                p: '1px 6px',
              }}
            >
              <Typography variant="subtitle1" color="black">
                {index + 1}/{resources.length}
              </Typography>
            </Box>
          </Stack>
        ))}
        {resourcesUploading && (
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{ p: 4, width: '100%' }}
          >
            <Loading />
          </Stack>
        )}
      </Stack>
      {!!error && <FormHelperText error>{error}</FormHelperText>}
    </CellFrame>
  )
}

const AudioUploadCell: Cell<{
  audioUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<PostCreateValues>
  error?: string
}> = ({ audioUploadInfo, setValue, error }) => {
  const { t } = useTranslation('consolePostCreate')

  const { onDrop, progress } = audioUploadInfo || {}

  return (
    <CellFrame sx={{ mb: 8 }}>
      <FileUploader
        t={t}
        fileUploadInfo={{
          type: 'audio',
          onDrop: () =>
            onDrop().then(resources => {
              const resource = resources?.[0]
              if (!resource) {
                return
              }

              setValue('resource', {
                tmpPath: resource.imageUploadPath,
              })
              return resource
            }),
          progress: progress || 0,
          description: t('audioUploaderDescription'),
          onClose: () => setValue('resource', { tmpPath: '' }),
        }}
        content={{
          title: t('audioUploaderTitle'),
        }}
      />
      {!!error && <FormHelperText error>{error}</FormHelperText>}
    </CellFrame>
  )
}

const VideoUploadCell: Cell<{
  videoId?: string | null
  setValue: UseFormSetValue<PostCreateValues>
  error?: string
  videoUploadInfo: FileUploadInfo
  resourcesUploading: boolean
}> = ({ videoId, setValue, error, videoUploadInfo, resourcesUploading }) => {
  const { t } = useTranslation('consolePostCreate')
  const videoRef = useRef<YouTube>(null)
  const { playingResource, playResource } = usePlayingResource()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openYoutubeUrlForm, setOpenYoutubeUrlForm] = useState(false)

  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('')

  useEffect(() => {
    if (playingResource !== videoId) {
      videoRef.current?.getInternalPlayer()?.pauseVideo()
    }
  }, [playingResource, videoId])

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const { getRootProps } = useDropzone({
    accept: config.upload.mimeTypes.video,
  })

  const onResetResource = () => {
    setValue('resource', defaultResourceValues.video)
    setYoutubeUrl('')
    setUploadedVideoUrl('')
  }

  return (
    <CellFrame sx={{ mb: 8 }}>
      <Stack direction="row" alignItems="center">
        <Icons.Video fontSize="large" color="primary" sx={{ pr: 2 }} />
        <Typography
          variant="h5"
          sx={{ textAlign: 'center', verticalAlign: 'center' }}
        >
          {t('attacheVideo')}
        </Typography>

        <IconButton.ThinPlus
          size="small"
          sx={{ ml: 'auto' }}
          onClick={handleClick}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem
            sx={{ width: 200 }}
            onClick={() => {
              setAnchorEl(null)

              videoUploadInfo.onDrop().then(resources => {
                const resource = resources?.[0]
                if (!resource) {
                  return
                }

                setUploadedVideoUrl(resource.imageUrl ?? '')
                setValue('resource', {
                  tmpPath: resource.imageUploadPath,
                })
                return resource
              })
            }}
          >
            <Box {...getRootProps()}>{t('uploadVideo')}</Box>
          </MenuItem>
          <MenuItem
            sx={{ width: 200 }}
            onClick={() => {
              setOpenYoutubeUrlForm(true)
              setAnchorEl(null)
            }}
          >
            {t('embedYoutubeVideo')}
          </MenuItem>
        </Menu>
      </Stack>

      {videoId && (
        <Box sx={{ position: 'relative', pt: 4 }}>
          <IconButton.Close
            bgcolor="grey.100"
            sx={{ position: 'absolute', top: 20, right: 8 }}
            onClick={onResetResource}
          />
          <YouTube
            id={videoId || ''}
            videoId={videoId || ''}
            opts={{
              width: '100%',
              height: '168px',
            }}
            ref={videoRef}
            onPlay={() => playResource(videoId ?? '')}
          />
        </Box>
      )}

      {resourcesUploading && (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ bgcolor: 'grey.50', width: '100%', height: 168, mt: 4 }}
        >
          <Loading />
        </Stack>
      )}

      {!resourcesUploading && uploadedVideoUrl && (
        <Box sx={{ position: 'relative', pt: 4 }}>
          <IconButton.Close
            bgcolor="grey.100"
            sx={{ position: 'absolute', top: 20, right: 8, zIndex: 2 }}
            onClick={onResetResource}
          />
          <video controls width="100%" height="168px">
            <source src={uploadedVideoUrl} type="video/mp4" />
          </video>
        </Box>
      )}

      {!!error && <FormHelperText error>{error}</FormHelperText>}

      <YoutubeUrlForm
        open={openYoutubeUrlForm}
        onClose={() => setOpenYoutubeUrlForm(false)}
        videoId={videoId}
        youtubeUrl={youtubeUrl}
        setYoutubeUrl={setYoutubeUrl}
        setValue={setValue}
        error={error}
      />
    </CellFrame>
  )
}

const ThumbnailUploadCell: Cell<{
  thumbnailUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<PostCreateValues>
  control: Control<PostCreateValues>
  error?: string
  thumbnailUploading: boolean
}> = ({
  thumbnailUploadInfo,
  control,
  setValue,
  error,
  thumbnailUploading,
}) => {
  const { t } = useTranslation('consolePostCreate')
  const [resource, setResource] = useState<Resource | null>(null)

  return (
    <CellFrame sx={{ mb: 8 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Icons.Image sx={{ color: 'primary.main' }} />
          <Typography variant="h5">{t('thumbnailUploaderTitle')}</Typography>
        </Stack>
        <IconButton.Plus
          size="small"
          disabled={thumbnailUploading}
          onClick={() =>
            thumbnailUploadInfo.onDrop().then(resources => {
              const uploadedResource = resources?.[0]
              if (!uploadedResource) {
                return
              }
              setValue('thumbnailPath', uploadedResource.imageUploadPath ?? '')
              setResource(uploadedResource)
            })
          }
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
        {t('thumbnailUploaderDescription')}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          overflowX: 'auto',
          pt: resource ? 5 : 0,
          ...styleSets.hideScrollbar(),
        }}
      >
        {!thumbnailUploading && resource && (
          <Stack
            sx={{ position: 'relative', bgcolor: 'grey.100', width: '100%' }}
          >
            <IconButton.Close
              bgcolor="grey.50"
              onClick={() => {
                setValue('thumbnailPath', null)
                setResource(null)
              }}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
            />
            <Avatar
              variant="square"
              src={resource.imageUrl}
              sx={{
                width: '100%',
                height: 200,
                img: { objectFit: 'contain' },
              }}
            />
          </Stack>
        )}
        {thumbnailUploading && (
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{ p: 4, width: '100%' }}
          >
            <Loading />
          </Stack>
        )}
      </Stack>
      {!!error && <FormHelperText error>{error}</FormHelperText>}
    </CellFrame>
  )
}

type ReserveInputCellProps = {
  artistId: string
  value: PostCreateValues['publishedAt']
  setValue: UseFormSetValue<PostCreateValues>
}
const ReserveInputCell: Cell<ReserveInputCellProps> = ({
  artistId,
  setValue,
  value,
}) => {
  const { t } = useTranslation('consolePostCreate')
  const [open, setOpen] = useState<boolean>(false)

  return (
    <CellFrame sx={{ py: 3 }}>
      <Button
        variant="text"
        startIcon={<Icons.Calendar sx={{ color: 'primary.main' }} />}
        onClick={() => setOpen(true)}
        sx={{ textAlign: 'left', alignItems: 'flex-start' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {value
            ? t('postPostedOn', {
                date: DateTime.formatDate(value, 'LL HH:mm') || '',
                fallback: 'このポストは{{-date}}に投稿されます。',
              })
            : t('postDateTime', '投稿日時を指定する')}
        </Stack>
      </Button>
      <ConsolePostReserve
        open={open}
        onClose={() => setOpen(false)}
        artistId={artistId}
        defaultValue={value}
        onSubmit={async value => {
          setValue('publishedAt', value.publishedAt as never)
        }}
        onDelete={() => {
          setValue('publishedAt', undefined as never)
        }}
      />
    </CellFrame>
  )
}

const ContentCell: Cell<{
  register: UseFormRegister<PostCreateValues>
  error?: FieldError
}> = ({ register, error }) => {
  return (
    <TextInputCell
      type="textarea"
      textFieldProps={{
        variant: 'standard',
        multiline: true,
        rows: 5,
        InputProps: {
          sx: {
            bgcolor: 'background.default',
          },
          disableUnderline: true,
        },
      }}
      titleProps={{ variant: 'h5', color: 'text.primary' }}
      error={error}
      {...register('content')}
    />
  )
}

type YoutubeUrlFormProps = {
  open: boolean
  onClose: () => void
  videoId?: string | null
  youtubeUrl: string
  setYoutubeUrl: Dispatch<SetStateAction<string>>
  setValue: UseFormSetValue<PostCreateValues>
  error?: string
}
const YoutubeUrlForm: Cell<YoutubeUrlFormProps> = ({
  open,
  onClose,
  videoId,
  youtubeUrl,
  setYoutubeUrl,
  setValue,
  error,
}) => {
  const { t } = useTranslation('consolePostCreate')
  const videoRef = useRef<YouTube>(null)
  const { playingResource, playResource } = usePlayingResource()

  const [previewVideoId, setPreviewVideoId] = useState('')

  useEffect(() => {
    if (playingResource !== videoId) {
      videoRef.current?.getInternalPlayer()?.pauseVideo()
    }
  }, [playingResource, videoId])

  useEffect(() => {
    const videoId = extractVideoId(youtubeUrl) ?? ''

    setPreviewVideoId(videoId)
  }, [youtubeUrl])

  return (
    <FullscreenModal open={open} onClose={onClose}>
      <ConsoleBaseLayout
        header={{
          title: t('youtubeUrlFormTitle'),
          left: (
            <IconButton.Back
              color="common.white"
              onClick={() => {
                !videoId && setYoutubeUrl('')
                onClose()
              }}
            />
          ),
          right: (
            <Button
              variant="text"
              onClick={() => {
                const videoId = extractVideoId(youtubeUrl)

                setValue('resource', { videoId })
                onClose()
              }}
              sx={{ color: 'common.white' }}
            >
              {t('add')}
            </Button>
          ),
        }}
        margin
      >
        <TextInputCell
          type="textarea"
          textFieldProps={{
            variant: 'standard',
            InputProps: {
              sx: {
                bgcolor: 'background.default',
              },
              disableUnderline: true,
            },
            placeholder: t('videoUploaderPlaceholder'),
            defaultValue: youtubeUrl,
          }}
          titleProps={{
            variant: 'h5',
            color: 'text.primary',
            children: t('videoUploaderTitle'),
            sx: { pt: 5 },
          }}
          onChange={async e => {
            setYoutubeUrl(e.target.value)
          }}
        />
        {previewVideoId && (
          <YouTube
            id={previewVideoId || ''}
            videoId={previewVideoId || ''}
            opts={{
              width: '100%',
              height: '168px',
            }}
            ref={videoRef}
            onPlay={() => playResource(previewVideoId ?? '')}
          />
        )}
        {!!error && <FormHelperText error>{error}</FormHelperText>}
      </ConsoleBaseLayout>
    </FullscreenModal>
  )
}

/**
 * extract youtube video id from youtube url
 * @param url
 */
export function extractVideoId(url: string): string | null {
  const res = url.match(
    /^https:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9-_]+)|^https:\/\/youtube\.com\/([a-zA-Z0-9-_]+)|^https:\/\/youtu\.be\/([a-zA-Z0-9-_]+)/
  )

  if (res) return res[1] || res[2] || res[3]

  return ''
}
